import { useCallback, useEffect, useState } from 'react';
import { CheckInResponse } from '../../../types';
import { searchCheckIns } from '../../../services/api/endpoints/checkins';

// Helper to get date N days ago in YYYY-MM-DD format
function getDateDaysAgo(days: number): string {
  const date = new Date();
  date.setDate(date.getDate() - days);
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

// Helper to get today in YYYY-MM-DD format
function getTodayYMD(): string {
  const date = new Date();
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function useCheckIns(
  token: string | null,
  fromDate?: string,  // YYYY-MM-DD format
  toDate?: string     // YYYY-MM-DD format
) {
  const [allData, setAllData] = useState<CheckInResponse[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!token) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      // Fetch check-ins with backend date filtering to avoid fetching too much data
      const searchParams = {
        page: 1,
        fromDate,
        toDate,
        orderBy: 'timestamp' as const,
        orderDirection: 'desc' as const,
        bypassCache: true,
      };

      // Fetch first page to get total count
      const firstPage = await searchCheckIns(token, searchParams);

      // Use total_pages directly from API response
      const totalPagesToFetch = firstPage.total_pages;
      
      // Collect all check-ins starting with first page
      let allCheckIns: CheckInResponse[] = [...(firstPage.items as CheckInResponse[])];
      
      // Fetch remaining pages if needed
      for (let currentPage = 2; currentPage <= totalPagesToFetch; currentPage++) {
        const pageParams = {
          page: currentPage,
          fromDate,
          toDate,
          orderBy: 'timestamp' as const,
          orderDirection: 'desc' as const,
          bypassCache: true,
        };
        
        const pageData = await searchCheckIns(token, pageParams);
        allCheckIns = [...allCheckIns, ...(pageData.items as CheckInResponse[])];
      }

      setAllData(allCheckIns);
    } catch (e) {
      console.error('Failed to load check-ins', e);
      setError(e instanceof Error ? e.message : 'Failed to load check-ins');
    } finally {
      setIsLoading(false);
    }
  }, [token, fromDate, toDate]);

  useEffect(() => {
    load();
  }, [load]);

  const refresh = useCallback(() => load(), [load]);

  return { allData, isLoading, error, refresh };
}

export default useCheckIns;
